import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Message, Spin, Switch, Space, Typography } from '@arco-design/web-react';
import { useTranslation } from 'react-i18next';
import { useOnboardingStatus } from './hooks/useOnboardingData';
import OnboardingLayout from './OnboardingLayout';
import IdentityStep from './steps/IdentityStep';
import TeacherDetailsStep from './steps/TeacherDetailsStep';
import ReviewStep from './steps/ReviewStep';
import './OnboardingWizard.css';

function OnboardingWizard({ previewMode = false }) {
  const { t } = useTranslation('onboarding');

  const STEPS = [
    { title: t('wizard.steps.personalData'), description: t('wizard.steps.personalDataDesc') },
    { title: t('wizard.steps.academicInfo'), description: t('wizard.steps.academicInfoDesc') },
    { title: t('wizard.steps.professionalProfile'), description: t('wizard.steps.professionalProfileDesc') },
    { title: t('wizard.steps.availability'), description: t('wizard.steps.availabilityDesc') },
    { title: t('wizard.steps.review'), description: t('wizard.steps.reviewDesc') }
  ];

  const [currentStep, setCurrentStep] = useState(1);
  const [freeMode, setFreeMode] = useState(previewMode);
  const initialStepSet = useRef(false);

  const {
    data: statusPayload,
    isLoading,
    refetch
  } = useOnboardingStatus({ enabled: !previewMode });

  const data = useMemo(() => ({
    identity: statusPayload?.identity_data || null,
    teacherDetails: statusPayload?.role_specific_data || null,
    documents: statusPayload?.documents || null
  }), [statusPayload]);

  const requestedChanges = useMemo(() =>
    Array.isArray(statusPayload?.requested_changes)
      ? statusPayload.requested_changes
      : [],
  [statusPayload]);

  // Determine initial step from loaded data (runs only once)
  useEffect(() => {
    if (previewMode || !statusPayload || initialStepSet.current) return;
    initialStepSet.current = true;

    const identity = statusPayload.identity_data || {};
    const teacher = statusPayload.role_specific_data || {};

    const isIdentityComplete = [
      'first_name',
      'last_name',
      'date_of_birth',
      'government_id',
      'government_id_type',
      'phone',
      'address',
      'city',
      'state',
      'postal_code',
      'country'
    ].every((field) => identity[field]);

    if (identity.government_id_type === 'OTHER' && !identity.government_id_type_other) {
      setCurrentStep(1);
      return;
    }

    const degrees = Array.isArray(teacher.degrees) ? teacher.degrees : [];
    const hasAcademic = degrees.length > 0 && degrees.every((degree) => (
      degree.level && degree.institution && degree.field && degree.graduation_year
    ));
    const sniMissing = teacher.is_sni_member && !teacher.sni_level;
    const hasProfessional = teacher.years_teaching_experience !== undefined
      && teacher.years_teaching_experience !== null;
    const hasAvailability = Array.isArray(teacher.availability_schedule)
      && teacher.availability_schedule.length > 0
      && teacher.max_hours_per_week;

    if (!isIdentityComplete) {
      setCurrentStep(1);
    } else if (!hasAcademic || sniMissing) {
      setCurrentStep(2);
    } else if (!hasProfessional) {
      setCurrentStep(3);
    } else if (!hasAvailability) {
      setCurrentStep(4);
    } else {
      setCurrentStep(5);
    }
  }, [statusPayload, previewMode]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  const handleNext = async () => {
    if (previewMode) {
      if (currentStep < STEPS.length) {
        setCurrentStep(prev => prev + 1);
        window.scrollTo(0, 0);
      }
      return;
    }

    await refetch();

    if (currentStep < STEPS.length) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = async (targetStep) => {
    if (previewMode) {
      if (typeof targetStep === 'number') {
        setCurrentStep(targetStep);
      } else {
        setCurrentStep(prev => Math.max(1, prev - 1));
      }
      window.scrollTo(0, 0);
      return;
    }

    await refetch();

    if (typeof targetStep === 'number') {
      setCurrentStep(targetStep);
    } else {
      setCurrentStep(prev => Math.max(1, prev - 1));
    }
    window.scrollTo(0, 0);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <IdentityStep
            key={`identity-${JSON.stringify(data.identity)?.substring(0, 50)}`}
            onNext={handleNext}
            initialData={data.identity}
            documents={data.documents}
            requestedChanges={requestedChanges}
            previewMode={previewMode}
            freeMode={freeMode}
          />
        );

      case 2:
        return (
          <TeacherDetailsStep
            key={`teacher-${JSON.stringify(data.teacherDetails)?.substring(0, 50)}`}
            onNext={handleNext}
            onBack={handleBack}
            section="academic"
            initialData={data.teacherDetails}
            documents={data.documents}
            requestedChanges={requestedChanges}
            previewMode={previewMode}
            freeMode={freeMode}
          />
        );

      case 3:
        return (
          <TeacherDetailsStep
            key={`teacher-professional-${JSON.stringify(data.teacherDetails)?.substring(0, 50)}`}
            onNext={handleNext}
            onBack={handleBack}
            section="professional"
            initialData={data.teacherDetails}
            documents={data.documents}
            requestedChanges={requestedChanges}
            previewMode={previewMode}
            freeMode={freeMode}
          />
        );

      case 4:
        return (
          <TeacherDetailsStep
            key={`teacher-availability-${JSON.stringify(data.teacherDetails)?.substring(0, 50)}`}
            onNext={handleNext}
            onBack={handleBack}
            section="availability"
            initialData={data.teacherDetails}
            documents={data.documents}
            requestedChanges={requestedChanges}
            previewMode={previewMode}
            freeMode={freeMode}
          />
        );

      case 5:
        return (
          <ReviewStep
            key={`review-${JSON.stringify(data)?.substring(0, 50)}`}
            onBack={handleBack}
            data={data}
            requestedChanges={requestedChanges}
            previewMode={previewMode}
            freeMode={freeMode}
          />
        );

      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <OnboardingLayout>
        <div className="onboarding-loading">
          <Spin size={40} />
          <p>{t('wizard.loading')}</p>
        </div>
      </OnboardingLayout>
    );
  }

  const handleStepClick = (stepIndex) => {
    setCurrentStep(stepIndex);
    window.scrollTo(0, 0);
  };

  return (
    <OnboardingLayout
      currentStep={currentStep}
      totalSteps={STEPS.length}
      stepTitle={STEPS[currentStep - 1]?.title || ''}
      onStepClick={handleStepClick}
      steps={STEPS}
    >
      <div className="onboarding-wizard">
        <div className="wizard-content">
          {previewMode && (
            <div style={{ marginBottom: 16 }}>
              <Message
                type="warning"
                content={t('wizard.simulationMode')}
                style={{ marginBottom: 12 }}
              />
              <Space align="center">
                <Switch checked={freeMode} onChange={setFreeMode} />
                <Typography.Text>{t('wizard.freeMode')}</Typography.Text>
              </Space>
            </div>
          )}
          {renderStep()}
        </div>
      </div>
    </OnboardingLayout>
  );
}

export default OnboardingWizard;
